/**
 * Citation-badge renderer.
 * Extracted from App.js in SPD v5.53 Phase D.
 *
 * Walks ReactMarkdown's `children` array, scans every string node for
 * [N] or legacy [Source N] tokens, and replaces each match with a small
 * cyan pill <button> that links to sources[N-1].url.
 *
 * Click behaviour respects the Safety View toggle:
 *   Safety OFF (default) -> opens URL in a new tab
 *   Safety ON            -> opens URL inside the in-app Safety View modal
 */
import React from 'react';

export const CITATION_RE = /\[(?:Source\s+)?(\d{1,2})\]/g;

export const renderCitedChildren = (children, sources, browseMode, setSafeBrowserState) => {
  if (children == null) return children;
  if (Array.isArray(children)) {
    return children.map((child, i) => (
      <React.Fragment key={i}>
        {renderCitedChildren(child, sources, browseMode, setSafeBrowserState)}
      </React.Fragment>
    ));
  }
  if (typeof children !== 'string') {
    // React element (e.g. <strong>{text}</strong>) — recurse into its kids.
    if (React.isValidElement(children) && children.props && children.props.children) {
      return React.cloneElement(
        children,
        children.props,
        renderCitedChildren(children.props.children, sources, browseMode, setSafeBrowserState)
      );
    }
    return children;
  }

  // children is a plain string here — split on the citation regex.
  const parts = [];
  let lastIndex = 0;
  let match;
  let tokenIdx = 0;
  CITATION_RE.lastIndex = 0;
  while ((match = CITATION_RE.exec(children)) !== null) {
    if (match.index > lastIndex) {
      parts.push(children.slice(lastIndex, match.index));
    }
    const n = parseInt(match[1], 10);
    const source = sources[n - 1];
    if (source && source.url) {
      parts.push(
        <button
          key={`cite-${tokenIdx}`}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (browseMode === 'safety') {
              setSafeBrowserState({ url: source.url, title: source.name || source.title || '' });
            } else {
              window.open(source.url, '_blank', 'noopener,noreferrer');
            }
          }}
          className="inline-flex items-center justify-center mx-0.5 px-1.5 py-0.5 text-xs font-semibold rounded bg-cyan-100 text-cyan-800 hover:bg-cyan-200 transition-colors align-baseline cursor-pointer"
          data-testid={`citation-${n}`}
        >
          [{n}]
        </button>
      );
    } else {
      // No matching source — leave the literal text in place so we don't
      // silently swallow content. Fail-safe over fail-pretty.
      parts.push(match[0]);
    }
    lastIndex = match.index + match[0].length;
    tokenIdx += 1;
  }
  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }
  return parts.length > 0 ? parts : children;
};
